import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps, Response, Country, PermissionEnum } from '@ui/src';
import { PspAccount } from '../../types/psp-account';
import { Account } from '../../types/account';
import { Currency } from '../../types/currency';
import { dictionariesApi, PermissionResponse } from './services';
import { PaymentType as PaymentTypeProps, PurposeCodeSettings } from '../types';
import { PspPartner } from '../../types/psp-partner';

export interface TransactionsDictionariesState {
  accounts?: SelectItemProps[];
  transactionAccounts?: Account[];
  accountsList?: Account[];
  currencies?: SelectItemProps[];
  currenciesList?: Currency[];
  countries?: SelectItemProps[];
  countriesList?: Country[];
  chargeTypes: SelectItemProps[];
  activePermissions?: PermissionEnum[];
  rateSources?: SelectItemProps[];
  transactionsTypes?: { id: string; name: string }[];
  baseCurrency?: Currency;
  pspPartnerAccounts?: SelectItemProps[];
  pspPartners?: PspPartner[];
  primaryAccounts?: PspAccount[];
  baseRateSettings?: Record<string, string>;
  paymentTypes?: PaymentTypeProps[];
  overdraftEnabled?: boolean;
  purposeCodesSettings?: PurposeCodeSettings[];

  whitelabel?: {
    data: {
      value: Record<string, string>;
    };
  };
}

const initialState: TransactionsDictionariesState = {
  chargeTypes: [
    {
      name: 'BEN',
      value: 'BEN',
    },
    {
      name: 'SHA',
      value: 'SHA',
    },
    {
      name: 'OUR',
      value: 'OUR',
    },
  ],
};

export const transactionsDictionariesSlice = createSlice({
  name: 'transactionsDictionaries',
  initialState,
  reducers: {
    setActivePermissions: (state, action: PayloadAction<PermissionEnum[]>) => {
      state.activePermissions = action.payload;
    },
    setPaymentTypes: (state, action: PayloadAction<PaymentTypeProps[]>) => {
      state.paymentTypes = action.payload;
    },
    setTransactionAccounts: (state, action: PayloadAction<Account[]>) => {
      state.transactionAccounts = action.payload;
    },
    setTransactionsCurrencies: (state, action: PayloadAction<Currency[]>) => {
      state.currencies = action.payload?.map((item) => ({
        name: item.name,
        value: item.id,
        suffix: item.description,
      }));

      state.currenciesList = action.payload?.map((item) => item);
    },
    setTransactionsBaseCurrency: (state, action: PayloadAction<Currency>) => {
      state.baseCurrency = action.payload;
    },
    setTransactionsCountries: (state, action: PayloadAction<Country[]>) => {
      state.countries = action.payload?.map((item) => ({
        name: item.name,
        value: item.id,
      }));

      state.countriesList = action.payload;
    },
    setTransactionsRateSources: (
      state,
      action: PayloadAction<
        {
          name: string;
          description: string;
          id: string;
        }[]
      >,
    ) => {
      state.rateSources = action?.payload?.map((item) => ({
        name: item.name,
        value: item.id,
        description: item.description,
      }));
    },
    setTransactionsSettings: (
      state,
      action: PayloadAction<{ key: string; data: { value: boolean } }[]>,
    ) => {
      const overdraftSetting = action.payload?.find(
        (item) => item.key === 'OVERDRAFT-ENABLED',
      );

      if (overdraftSetting) {
        if (
          overdraftSetting.data.value === undefined ||
          overdraftSetting.data.value === null
        ) {
          state.overdraftEnabled = false;
        } else {
          state.overdraftEnabled = overdraftSetting.data.value;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsDictionaries.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            accounts: Response<Account>;
            transactionTypes: Response<{ id: string; name: string }>;
          }>,
        ) => {
          state.accounts = action.payload.accounts?.data?.edges?.map(
            (item) => ({
              name: item.node.description,
              description: '',
              value: item.node.fullNumber,
              suffix: item.node.fullNumber,
            }),
          );

          if (action?.payload?.transactionTypes) {
            state.transactionsTypes =
              action.payload.transactionTypes.data?.edges.map(
                (item) => item.node,
              );
          }

          state.accountsList = action.payload?.accounts?.data?.edges?.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsRateSourceSettings
          .matchFulfilled,
        (state, action: PayloadAction<{ data: Record<string, string> }>) => {
          state.baseRateSettings = action.payload?.data;
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsDictionariesWithoutGL
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{
            pspPartnersAccounts: Response<PspAccount>;
          }>,
        ) => {
          if (action.payload?.pspPartnersAccounts) {
            const nextPspPartnerAccounts: SelectItemProps[] = [];

            action.payload.pspPartnersAccounts?.data?.edges
              ?.filter(
                (item) => !item?.node?.account?.description.includes('funds'),
              )
              ?.forEach((item) => {
                if (
                  !nextPspPartnerAccounts.find(
                    (pspPartnerAccount) =>
                      pspPartnerAccount.value ===
                      item.node?.account?.fullNumber,
                  )
                ) {
                  nextPspPartnerAccounts.push({
                    value: item.node.account?.fullNumber || '',
                    name: item.node.description || '',
                    description: '',
                    suffix: item.node.account?.fullNumber,
                  });
                }
              });

            state.pspPartnerAccounts = nextPspPartnerAccounts?.sort(
              (a, b) =>
                Number((a.value as string).replaceAll('.', '')) -
                Number((b.value as string).replaceAll('.', '')),
            );
          }
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsAccounts.matchFulfilled,
        (state, action: PayloadAction<Response<Account>>) => {
          if (!state.accounts) {
            state.accounts = action.payload?.data?.edges?.map((item) => ({
              name: item.node.description,
              description: '',
              value: item.node.fullNumber,
              suffix: item.node.fullNumber,
            }));

            state.accountsList = action.payload?.data?.edges?.map(
              (item) => item.node,
            );
          }
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsCurrencies.matchFulfilled,
        (state, action: PayloadAction<Response<Currency>>) => {
          state.currencies = action.payload?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node.id,
            suffix: item.node.description,
          }));

          state.currenciesList = action.payload?.data?.edges?.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsPurposeCodesSettings
          .matchFulfilled,
        (state, action: PayloadAction<Response<PurposeCodeSettings>>) => {
          state.purposeCodesSettings = action.payload.data.edges.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsPrimaryAccounts.matchFulfilled,
        (state, action: PayloadAction<Response<PspAccount>>) => {
          if (!state.primaryAccounts) {
            state.primaryAccounts = action.payload?.data?.edges?.map(
              (item) => item.node,
            );
          }
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsCountries.matchFulfilled,
        (state, action: PayloadAction<Response<Country>>) => {
          state.countries = action.payload?.data?.edges?.map((item) => ({
            name: item.node.name,
            value: item.node.id,
          }));

          state.countriesList = action.payload?.data?.edges?.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsPspPartners.matchFulfilled,
        (state, action: PayloadAction<Response<PspPartner>>) => {
          if (!state.pspPartners) {
            state.pspPartners = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          }
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsRateSource.matchFulfilled,
        (
          state,
          action: PayloadAction<
            Response<{
              name: string;
              description: string;
              id: string;
            }>
          >,
        ) => {
          state.rateSources = action.payload.data.edges.map((item) => ({
            name: item.node.name,
            value: item.node.id,
            description: item.node.description,
          }));
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsPermissions.matchFulfilled,
        (state, action: PayloadAction<{ data: PermissionResponse }>) => {
          state.activePermissions = action.payload.data.permissions;

          localStorage.setItem(
            'activePermissions',
            JSON.stringify(action.payload.data.permissions),
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsBaseCurrency.matchFulfilled,
        (state, action: PayloadAction<{ data: Currency }>) => {
          state.baseCurrency = action.payload.data;
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsPaymentTypes.matchFulfilled,
        (state, action: PayloadAction<Response<PaymentTypeProps>>) => {
          state.paymentTypes = action?.payload?.data?.edges?.map(
            (item) => item.node,
          );
        },
      )
      .addMatcher(
        dictionariesApi.endpoints.getTransactionsWhitelabel.matchFulfilled,
        (
          state,
          action: PayloadAction<{
            data: {
              data: {
                value: Record<string, string>;
              };
            };
          }>,
        ) => {
          state.whitelabel = action.payload.data;
        },
      );
    builder.addMatcher(
      dictionariesApi.endpoints.getTransactionsSettings.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{ key: string; data: { value: boolean } }>
        >,
      ) => {
        const overdraftSetting = action.payload.data.edges?.find(
          (item) => item.node.key === 'OVERDRAFT-ENABLED',
        );

        if (overdraftSetting) {
          if (
            overdraftSetting.node.data.value === undefined ||
            overdraftSetting.node.data.value === null
          ) {
            state.overdraftEnabled = false;
          } else {
            state.overdraftEnabled = overdraftSetting.node.data.value;
          }
        }
      },
    );
  },
});

export const {
  setActivePermissions,
  setPaymentTypes,
  setTransactionsBaseCurrency,
  setTransactionsCountries,
  setTransactionsCurrencies,
  setTransactionsRateSources,
  setTransactionsSettings,
  setTransactionAccounts,
} = transactionsDictionariesSlice.actions;

export default transactionsDictionariesSlice.reducer;
